import { AfterViewInit, ChangeDetectionStrategy, Component, ComponentRef, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, QueryList, SimpleChanges, TemplateRef, Type, ViewChild, ViewChildren, ViewContainerRef } from "@angular/core";
import { ElementUtilities } from "@common/lib/utilities/element-utilities";
import { IDxMenuItemClicked } from "@common/ux/dx.types";
import { ValueChangedEvent } from "devextreme/ui/check_box";
import { Item, Orientation, SubmenuHiddenEvent, SubmenuShownEvent } from "devextreme/ui/menu";
import { DxMenuComponent } from "devextreme-angular";
import { BaseComponent } from "../base.component/base.component";

export interface IAdaptMenuItem extends Item {
    tooltip?: string;

    templateType?: Type<any>;
    templateInputKey?: string;
    templateId?: string;
    componentRef?: ComponentRef<any>;
    templateTypeHeightSet?: boolean;
    lastSubmenuShownEvent?: SubmenuShownEvent;

    templateRef?: TemplateRef<any>;
    templateData?: any;

    // DX handles calling this for us
    onClick?: (e: IDxMenuItemClicked<IAdaptMenuItem>) => void;

    /** If this is defined, then a checkbox will automatically be rendered for the item */
    isChecked?: boolean;

    /**
     * @deprecated use separatorTop or separatorBottom as its clearer
     */
    separator?: boolean;

    separatorTop?: boolean;
    separatorBottom?: boolean;

    /** Will be called when isChecked changes */
    onIsCheckedChange?: (isChecked: boolean) => void;

    // if the menu item should have padding on itself
    padding?: boolean;

    items?: IAdaptMenuItem[]; // without this, items will be dxMenuItem[] which won't allow onClick
}

@Component({
    selector: "adapt-menu",
    templateUrl: "./menu.component.html",
    styleUrls: ["./menu.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class MenuComponent extends BaseComponent implements OnChanges, AfterViewInit {
    public static readonly StandardRootMenu: IAdaptMenuItem = {
        text: "",
        icon: "fal fa-fw fa-2x fa-ellipsis-h",
    };

    public static readonly SmallRootMenu: IAdaptMenuItem = {
        text: "",
        icon: "fal fa-fw fa-ellipsis-h small-root-menu",
    };

    public static readonly SmallAddMenu: IAdaptMenuItem = {
        text: "",
        icon: "fal fa-fw fa-plus-square",
    };

    @Input() public items?: IAdaptMenuItem[];

    @Input() public showOnHover = false;
    @Input() public disabled = false;
    @Input() public orientation: Orientation = "horizontal";
    @Output() public visibleChanged = new EventEmitter<boolean>();

    @ViewChild(DxMenuComponent) private menuComponent?: DxMenuComponent;

    @ViewChildren("templateContainer", { read: ViewContainerRef }) public templateContainersList?: QueryList<ViewContainerRef>;

    public constructor(elementRef: ElementRef) {
        super(elementRef);
    }

    // this won't stop propagation to dxMenu - only stop propagate event to parent container
    @HostListener("click", ["$event"])
    public stopPropagation(e: Event) {
        e.stopPropagation();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.items) {
            this.processMenuItems(this.items);
        }
    }

    public ngAfterViewInit() {
        this.templateContainersList?.changes.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.processMenuItems(this.items));
    }

    public onSubmenuHidden(e: SubmenuHiddenEvent) {
        const itemData = e.itemData as IAdaptMenuItem;
        if (itemData) {
            itemData.lastSubmenuShownEvent = undefined;
        }

        this.visibleChanged.emit(false);
    }

    public onSubmenuShown(e: SubmenuShownEvent) {
        const itemData = e.itemData as IAdaptMenuItem;
        itemData.lastSubmenuShownEvent = e;
        itemData.items?.filter((item) => !!item.templateType).forEach((item) => item.lastSubmenuShownEvent = e);

        if (itemData?.items?.find((item: IAdaptMenuItem) => !!item.templateType || !!item.templateRef)) {
            // submenu contains a templateType (angular component loaded dynamically)
            if (e.submenuContainer && !itemData.templateTypeHeightSet) {
                // do this once due to delayed angular component initialization
                // (need it each time templateRef is used or recent labels menu looks dodgy)
                itemData.templateTypeHeightSet = !itemData.items.some((item: IAdaptMenuItem) => !!item.templateRef);
                const submenuContainer = e.submenuContainer as unknown as JQuery<HTMLElement>;
                submenuContainer.css("height", "auto");
                if (itemData.templateTypeHeightSet && itemData.items.length === 1) {
                    // first condition is to ensure this is not setting it for the set labels submenu as that's also having a single templateRef item
                    submenuContainer.addClass("adapt-submenu-with-single-template-type");
                }

                setTimeout(() => {
                    // next cycle so auto height above can be applied first
                    const submenuTop = submenuContainer.offset()?.top ?? 0;
                    let submenuHeight = submenuContainer.outerHeight() ?? 0;
                    const windowHeight = window.innerHeight;
                    const parentContainer = submenuContainer.parent();
                    if (submenuTop + submenuHeight > windowHeight) {
                        // submenu cross off the window boundary - need to resize it
                        const parentTop = parentContainer.offset()?.top ?? 0;
                        const maxHeight = (parentTop > windowHeight / 2)
                            // from top of the window to the bottom of submenu parent
                            ? parentTop + parentContainer.height()! - 10 // dxMenu submenu has a 10px gap - so this newly height calculates the same as dx
                            // from the top of the submenu parent to bottom of window
                            : windowHeight - parentTop - 10;

                        if (submenuHeight > maxHeight) {
                            submenuContainer.css("height", maxHeight);
                            submenuHeight = maxHeight;
                        }

                        if (submenuTop > windowHeight / 2) {
                            submenuContainer.css("top", (parentContainer.height() ?? 0) - submenuHeight);
                        }
                    }

                    if (itemData.templateTypeHeightSet) {
                        // this is to move the submenu horizontally if it's going off the window or parent container / submenu resize
                        // - not needed when using templateRef
                        const translateString = submenuContainer.css("transform");
                        // Extract the translate values from the transform matrix string
                        // The match method with the regex /matrix.*\((.+)\)/ is used to find the matrix values within the transform string.
                        // The regex captures everything inside the parentheses of the matrix function.
                        // The captured values are then split by commas to get individual transform values.
                        // The translateX value is at index 4 and translateY value is at index 5 of the resulting array.
                        const translateValues = translateString.match(/matrix.*\((.+)\)/)?.[1].split(",") ?? [];
                        const translateX = Math.round(parseFloat(translateValues[4]) || 0);
                        const parentContainerWidth = Math.round(parentContainer.width() ?? 0) + 1;
                        const windowWidth = window.innerWidth;
                        const parentLeft = parentContainer.offset()?.left ?? 0;
                        const submenuWidth = Math.round(submenuContainer.width() ?? 0);
                        if ((parentLeft + parentContainerWidth + submenuWidth) <= windowWidth) {
                            // menu to the right of the parent
                            if (translateX !== parentContainerWidth) {
                                submenuContainer.css("transform", `translate(${parentContainerWidth}px, ${translateValues[5] || 0}px)`);
                            }
                        } else {
                            if (translateX !== -submenuWidth) {
                                submenuContainer.css("transform", `translate(${-submenuWidth}px, ${translateValues[5] || 0}px)`);
                            }
                        }
                    }
                });
            }
        }
    }

    public refresh() {
        this.processMenuItems(this.items);

        // need these to get dx to update if we are removing/adding items
        this.menuComponent?.instance.option("items", this.items);
        this.menuComponent?.instance.repaint();
    }

    public useCheckbox(item: IAdaptMenuItem) {
        return (typeof item.isChecked === "boolean");
    }

    public onCheckboxValueChanged(e: ValueChangedEvent, item: IAdaptMenuItem) {
        // no event will be present if change comes from other source
        if (e.event) {
            if (item.onIsCheckedChange) {
                item.onIsCheckedChange(!!item.isChecked);
            }

            if (item.onClick) {
                // this would be otherwise called from dx if is from clicking on the menu item
                item.onClick(e as unknown as IDxMenuItemClicked<IAdaptMenuItem>);
            }

            e.event?.stopPropagation(); // don't close the menu when clicking on the checkbox
        }
    }

    public onItemClick(e: IDxMenuItemClicked<IAdaptMenuItem>) {
        const item = e.itemData!;
        if (typeof item.isChecked === "boolean") {
            // this is for user clicking on the menu item - not on the checkbox
            item.isChecked = !item.isChecked;

            if (item.onIsCheckedChange) {
                item.onIsCheckedChange(item.isChecked);
            }
        }
    }

    private checkInitializeTemplateType(item: IAdaptMenuItem) {
        if (item.templateType && item.templateId) {
            const templateContainer = this.templateContainersList?.find((tc) => tc.element.nativeElement.getAttribute("templateId") === item.templateId);
            if (templateContainer && !ElementUtilities.isDetached(templateContainer.element.nativeElement)) {
                if (!item.componentRef || item.componentRef.hostView.destroyed) {
                    item.componentRef = templateContainer.createComponent(item.templateType!);

                    if (item.lastSubmenuShownEvent && item.componentRef?.instance.componentChanged$) {
                        item.componentRef.instance.componentChanged$.pipe(
                            this.takeUntilDestroyed(),
                        ).subscribe(() => {
                            const parentItemData = item.lastSubmenuShownEvent?.itemData as IAdaptMenuItem;
                            if (parentItemData) {
                                parentItemData.templateTypeHeightSet = false;
                                if (parentItemData?.lastSubmenuShownEvent) {
                                    // submenu still showing - call this to resize if required - otherwise it will be called next time it is popped
                                    this.onSubmenuShown(parentItemData.lastSubmenuShownEvent);
                                }
                            }
                        });
                    }
                }

                if (item.templateInputKey) {
                    item.componentRef.setInput(item.templateInputKey, item.templateData);
                }
            }
        }
    }

    private processMenuItems(items?: IAdaptMenuItem[]) {
        if (!items) {
            return;
        }

        let hasIcons = false;

        for (const item of items) {
            if (item.icon) {
                hasIcons = true;
            }

            this.checkInitializeTemplateType(item);

            // use an empty placeholder if other menu items have icons
            item.icon = (hasIcons && !item.icon)
                ? "fal fa-fw"
                : item.icon;

            if (Array.isArray(item.items)) {
                this.processMenuItems(item.items);
            }
        }
    }
}
